import React from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Description as DescriptionIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import { QAItem } from '../types/qa';
import {
  formatRetrievalDistance,
  averageRetrievalDistanceLabel,
  sortRetrievalSourcesByDistance,
  RETRIEVAL_DISTANCE_TOOLTIP,
  AVERAGE_RETRIEVAL_DISTANCE_TOOLTIP,
  RETRIEVAL_SCORE_LEGEND,
} from '../utils/qaHelpers';
import { CopyAnswerWithOriginalMenu } from './CopyAnswerWithOriginalMenu';

interface AnswerSectionProps {
  item: QAItem;
  showCopyButton?: boolean;
}

const AnswerSection: React.FC<AnswerSectionProps> = ({ item, showCopyButton = false }) => {
  const avgRetrievalDistance = averageRetrievalDistanceLabel(item.retrieval_sources);
  const sortedRetrievalSources = sortRetrievalSourcesByDistance(item.retrieval_sources);

  return (
    <>
      {/* System Generated Answer */}
      {item.system_generated_ans && (
        <Box sx={{ mb: 1.5, position: 'relative' }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: '#333',
            }}
          >
            System generated answer:
          </Typography>
          <Box
            sx={{
              border: '1px solid #d9dede',
              borderRadius: '4px',
              p: 2,
              backgroundColor: '#f9f9f9',
              minHeight: '60px',
              maxHeight: '150px',
              overflowY: 'auto',
              pr: showCopyButton ? 12 : 2,
              pb: showCopyButton ? 4 : 2,
            }}
          >
            <Typography variant="body2" sx={{ color: '#333', whiteSpace: 'pre-wrap' }}>
              {item.system_generated_ans}
            </Typography>
          </Box>
          {showCopyButton && (
            <Box sx={{ position: 'absolute', bottom: 12, right: 12 }}>
              <CopyAnswerWithOriginalMenu
                text={item.system_generated_ans || ''}
                questionLocale={item.question_locale}
                qafId={item.qaf_id}
                accent="legacy"
              />
            </Box>
          )}
        </Box>
      )}

      {/* Retrieval sources (expandable) */}
      <Accordion
        disableGutters
        elevation={0}
        sx={{
          border: '1px solid #d9dede',
          borderRadius: '4px',
          mb: 1.5,
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: '#48a1ad' }} />}
          sx={{
            minHeight: 48,
            px: 2,
            '&.Mui-expanded': { minHeight: 48 },
            '& .MuiAccordionSummary-content': {
              my: 1,
              '&.Mui-expanded': { my: 1 },
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <DescriptionIcon sx={{ fontSize: 20, color: '#48a1ad' }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
              Retrieval sources
            </Typography>
            <Chip
              size="small"
              label={(item.retrieval_sources?.length ?? 0).toString()}
              sx={{
                height: 22,
                fontSize: '0.75rem',
                backgroundColor: '#e8f4f5',
                color: '#156C86',
              }}
            />
            {avgRetrievalDistance && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
                  Avg. vector distance: {avgRetrievalDistance}
                </Typography>
                <Tooltip title={AVERAGE_RETRIEVAL_DISTANCE_TOOLTIP} arrow placement="top">
                  <IconButton
                    size="small"
                    aria-label="About average vector distance"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ p: 0.25, color: '#666' }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
          {sortedRetrievalSources.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="caption" sx={{ color: '#666', lineHeight: 1.5, mb: 0.5 }}>
                {RETRIEVAL_SCORE_LEGEND}
              </Typography>
              {sortedRetrievalSources.map((src, idx) => (
                <Box
                  key={`${src.source_display_name}-${idx}`}
                  sx={{
                    border: '1px solid #d9dede',
                    borderRadius: '4px',
                    p: 2,
                    backgroundColor: '#fafafa',
                  }}
                >
                  <Typography variant="body2" sx={{ color: '#333', fontWeight: 600 }}>
                    {src.source_display_name || 'Source'}
                    {typeof src.relevance_score === 'number' && (
                      <Box
                        component="span"
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, ml: 0.5 }}
                      >
                        <Typography component="span" variant="body2" sx={{ color: '#666' }}>
                          · Vector distance: {formatRetrievalDistance(src.relevance_score)}
                        </Typography>
                        <Tooltip title={RETRIEVAL_DISTANCE_TOOLTIP} arrow placement="top">
                          <IconButton
                            size="small"
                            component="span"
                            aria-label="About vector distance"
                            sx={{ p: 0.25, color: '#666' }}
                          >
                            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Typography>
                  {src.text_excerpt ? (
                    <Typography variant="body2" sx={{ color: '#666', mt: 0.5, whiteSpace: 'pre-wrap' }}>
                      {src.text_excerpt}
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#999', mt: 0.5 }}>
                      No excerpt available.
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: '#999' }}>
              No sources retrieved for this question.
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {/* User Edited Answer */}
      {item.user_edited_ans && (
        <Box sx={{ mb: 1.5, position: 'relative' }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: '#333',
            }}
          >
            User edited answer:
          </Typography>
          <Box
            sx={{
              border: '1px solid #d9dede',
              borderRadius: '4px',
              p: 2,
              backgroundColor: '#ffffff',
              minHeight: '60px',
              maxHeight: '150px',
              overflowY: 'auto',
              pr: showCopyButton ? 12 : 2,
              pb: showCopyButton ? 4 : 2,
            }}
          >
            <Typography variant="body2" sx={{ color: '#333', whiteSpace: 'pre-wrap' }}>
              {item.user_edited_ans}
            </Typography>
          </Box>
          {showCopyButton && (
            <Box sx={{ position: 'absolute', bottom: 12, right: 12 }}>
              <CopyAnswerWithOriginalMenu
                text={item.user_edited_ans || ''}
                questionLocale={item.question_locale}
                qafId={item.qaf_id}
                accent="legacy"
              />
            </Box>
          )}
        </Box>
      )}

      {/* Admin Edited Answer */}
      {item.admin_edited_ans && (
        <Box sx={{ mb: 1.5, position: 'relative' }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: '#333',
            }}
          >
            Admin edited answer:
          </Typography>
          <Box
            sx={{
              border: '1px solid #d9dede',
              borderRadius: '4px',
              p: 2,
              backgroundColor: '#e8f5e9',
              minHeight: '60px',
              maxHeight: '150px',
              overflowY: 'auto',
              pr: showCopyButton ? 12 : 2,
              pb: showCopyButton ? 4 : 2,
            }}
          >
            <Typography variant="body2" sx={{ color: '#333', whiteSpace: 'pre-wrap' }}>
              {item.admin_edited_ans}
            </Typography>
          </Box>
          {showCopyButton && (
            <Box sx={{ position: 'absolute', bottom: 12, right: 12 }}>
              <CopyAnswerWithOriginalMenu
                text={item.admin_edited_ans || ''}
                questionLocale={item.question_locale}
                qafId={item.qaf_id}
                accent="legacy"
              />
            </Box>
          )}
        </Box>
      )}

      {/* Feedback Reason */}
      {item.feedback_reason && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: '#333',
            }}
          >
            Feedback reason:
          </Typography>
          <Box
            sx={{
              border: '1px solid #d9dede',
              borderRadius: '4px',
              p: 2,
              backgroundColor: '#ffffff',
              minHeight: '40px',
            }}
          >
            <Typography variant="body2" sx={{ color: '#666', whiteSpace: 'pre-wrap' }}>
              {item.feedback_reason}
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
};

export default AnswerSection;

